import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookingsService,
  type Booking,
  type BookingStatus,
} from '../../shared/services/bookings.service';

export type BookingScope = 'customer' | 'provider';

export type StatusFilter = 'ALL' | BookingStatus;

const QUERY_KEY = (scope: BookingScope, filter: StatusFilter) =>
  ['bookings', scope, filter] as const;

const FETCHER: Record<
  BookingScope,
  (status: BookingStatus | undefined) => Promise<Booking[]>
> = {
  customer: async (status) =>
    (await bookingsService.listMine({ status, page: 0, size: 100 })).data,
  provider: async (status) =>
    (await bookingsService.listIncoming({ status, page: 0, size: 100 })).data,
};

/**
 * Shared list hook for the customer and provider booking screens. Owns the
 * filter tab state, the react-query cache key and the invalidation helper
 * used by transition mutations to refresh the list.
 */
export function useBookingList(scope: BookingScope, initial: StatusFilter = 'ALL') {
  const [filter, setFilter] = useState<StatusFilter>(initial);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY(scope, filter),
    queryFn: () => FETCHER[scope](filter === 'ALL' ? undefined : filter),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['bookings', scope] });

  return {
    filter,
    setFilter,
    bookings: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    invalidate,
  };
}
